/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import _ from 'lodash';
import type { FC, MouseEventHandler } from 'react';
import { useCallback, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import type { Column } from 'react-table';

import {
    usePrepareColumns,
    useValueBasedOnWindowSize,
    ExportContextProvider,
    theme,
    useAuthContext,
} from '@catalyze/ui';
import { useUpdateEdmStatusesMutation } from 'api/edm/updateEdmStatuses';
import { updateLeadStatuses } from 'api/leads/updatedStatuses';
import { EdmStatus } from 'api/types/edm';
import type { ILead, ILeadsSortParams } from 'api/types/lead';
import { Status } from 'api/types/lead';
import type { IFilters } from 'components/Filter';
import { Filter } from 'components/Filter';
import { useCurrentRoute } from 'components/Filter/utils/useCurrentRoute';
import { SelectableColumns, ColumnsSortId } from 'constants/columns';
import type { Filters } from 'constants/filters';
import type { LeadGroups } from 'constants/leadGroups';
import { LeadGroupsToLeadStatus } from 'constants/leadGroups';
import {
    filtersInitialState,
    useFilterContext,
    updateFiltersAction,
} from 'features/RealEstate/context/FilterContext';
import type {
    TFilterState,
    TAvailableRoutes,
} from 'features/RealEstate/context/FilterContext';
import { edmStatusColor } from 'utils/edmStatusColor';

import { columnsConfig, columnsPriority } from './helpers/columnsConfig';
import { createFilterRegex } from './helpers/createFilterRegex';
import { prepareCheckboxes } from './helpers/prepareCheckboxes';
import { prepareData } from './helpers/prepareData';
import {
    prepareExportData,
    prepareSelectAllExportData,
} from './helpers/prepareExportData';
import {
    TableCard,
    StyledHeader,
    StyledTable,
    StyledTextWrapper,
} from './styled';

const statuses = [
    Status.RECOMMENDED,
    Status.CONTACTED,
    Status.ACTIVE,
    Status.LISTED,
    Status.SOLD,
    Status.ARCHIVED,
];

export interface ITable {
    filters?: IFilters;
    tableData: ILead[];
    tableColumns: any[];
    filterEnabledFeatures?: string[];
    leadGroup?: LeadGroups | undefined;
    fetchExportData: any;
    nrOfResults?: number;
    nrOfLoadedResults?: number;
    displayedMarketValue?: number | string;
    totalMarketValue?: string;
    isAllSelected?: boolean;
    isLoadingInitially?: boolean;
    canLoadMore?: boolean;
    onSelectAll?: (isSelected: boolean) => void;
    onSortApply?: (sortParams?: ILeadsSortParams) => void;
    onReachedEnd?: () => void;
    onSearchHandler?: (search: string) => void;
    onFiltersChange?: (filters: Record<Filters, TFilterState>) => void;
    onPropertyClick: (propertyId: string) => MouseEventHandler;
    showDnc?: boolean;
}

export const Table: FC<ITable> = ({
    tableData,
    filters,
    tableColumns,
    filterEnabledFeatures,
    leadGroup,
    displayedMarketValue,
    totalMarketValue,
    nrOfResults,
    nrOfLoadedResults,
    onSearchHandler,
    onFiltersChange,
    onPropertyClick,
    ...props
}) => {
    const { user } = useAuthContext();
    const queryClient = useQueryClient();
    const [numberOfColumns] = useValueBasedOnWindowSize<number>({
        0: 3,
        374: 4,
        767: 5,
        1023: 6,
        1279: 7,
        1439: tableColumns.length,
    });
    const { filterDispatch } = useFilterContext();
    const currentRoute = useCurrentRoute();
    const checkboxes = useMemo(() => prepareCheckboxes(), []);
    const data = useMemo(
        () =>
            prepareData(tableData, displayedMarketValue ?? 0, totalMarketValue),
        [tableData, displayedMarketValue, totalMarketValue]
    );
    type TableData = typeof data[0];
    const columns = usePrepareColumns<TableData>(
        tableColumns,
        numberOfColumns,
        columnsConfig as Array<Column<TableData>>,
        columnsPriority
    );

    const filterData = useCallback(
        (
            data: ReturnType<typeof prepareExportData>,
            selectedOptions: string[]
        ) => {
            const testRegex = createFilterRegex(selectedOptions);
            const filteredData = data.map((row) => {
                return Object.keys(row)
                    .filter((key) => !testRegex.test(key))
                    .reduce<Partial<typeof row>>(
                        (acc, key) => ({
                            ...acc,
                            [key]: row[key as keyof typeof row],
                        }),
                        {}
                    );
            });
            return filteredData;
        },
        []
    );

    const markAsOptions = useMemo(
        () =>
            Object.values(EdmStatus).map((edmStatus) => ({
                value: edmStatus as EdmStatus,
                label: edmStatus,
                color: edmStatusColor({ theme, status: edmStatus }),
            })),
        []
    );

    const menuStatus = useMemo(() => {
        const statusCopy = _.clone(statuses);
        // eslint-disable-next-line max-len
        return statusCopy
            .filter(
                // @ts-expect-error error
                (status) => status !== LeadGroupsToLeadStatus[leadGroup || '']
            )
            .map((status) => [status]);
    }, [leadGroup]);

    const { mutate: updateStatuses } = useUpdateEdmStatusesMutation({
        onSuccess: async () => {
            await queryClient.resetQueries('property-leads');
            await queryClient.resetQueries('infinitePropertyLeads');
        },
    });

    return (
        <TableCard>
            <ExportContextProvider
                initialState={{
                    filterData,
                    excludeOptions: SelectableColumns,
                    markAsOptions,
                    onExport: ({ rows, markAs }) => {
                        const propertyIds = rows.map(
                            (lead) => lead['Property ID']
                        );
                        if (markAs?.value) {
                            updateStatuses({
                                propertyLeadIds: propertyIds,
                                status: markAs?.value as EdmStatus,
                            });
                        }
                    },
                }}
            >
                <StyledTable
                    tableData={data}
                    tableColumns={columns}
                    tableHiddenColumns={{}}
                    tableCheckboxes={checkboxes}
                    tableRightAlignedHeaderCellIds={[
                        ColumnsSortId.MARKET_VALUE,
                        ColumnsSortId.TOTAL_MARKET_VALUE,
                    ]}
                    clickProps={{
                        functor: onPropertyClick,
                        columnName: ColumnsSortId.ADDRESS,
                    }}
                    dataGroup={leadGroup}
                    nrOfResults={nrOfResults}
                    nrOfLoadedResults={nrOfLoadedResults}
                    onUpdateStatuses={async (...args: any) => {
                        await updateLeadStatuses({
                            ids: args[0].ids,
                            newStatus: args[0].newStatus,
                        });
                        filterDispatch(
                            updateFiltersAction(
                                currentRoute as TAvailableRoutes,
                                filtersInitialState
                            )
                        );
                        await queryClient.invalidateQueries(
                            'property-leads-worth'
                        );
                        await queryClient.invalidateQueries(
                            'property-leads-filters'
                        );
                        await queryClient.invalidateQueries(
                            'infinitePropertyLeads'
                        );
                        await queryClient.invalidateQueries('property-leads');
                    }}
                    emptyMessage={
                        undefined ?? `You don't have any ${leadGroup}`
                    }
                    prepareExportData={prepareExportData}
                    prepareSelectAllExportData={prepareSelectAllExportData}
                    menuStatuses={menuStatus}
                    renderTableFilter={() => (
                        <StyledHeader>
                            <Filter
                                filters={filters}
                                onFiltersChange={onFiltersChange}
                                enabledFeatures={filterEnabledFeatures}
                                onSearchHandler={onSearchHandler}
                            />
                            <StyledTextWrapper
                                style={{
                                    marginLeft: '35px',
                                    fontWeight: 'bold',
                                    fontSize: '0.75rem',
                                }}
                            >
                                <table width="100%">
                                    <tbody>
                                        <tr>
                                            <td align="left">
                                                <span
                                                    role="img"
                                                    aria-label="flag"
                                                >
                                                    🚩
                                                </span>{' '}
                                                - Listed or Sold
                                            </td>
                                            <td align="right">{`${nrOfLoadedResults} / ${nrOfResults} Results`}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </StyledTextWrapper>
                        </StyledHeader>
                    )}
                    exportChipSelectOptions={SelectableColumns}
                    exportFilterData={filterData}
                    hideHeaderElement
                    hideFooterElement={false}
                    showDnc={user?.showDnc}
                    {...props}
                />
            </ExportContextProvider>
        </TableCard>
    );
};

Table.defaultProps = {
    nrOfLoadedResults: 0,
    nrOfResults: 0,
};
